import { formatDate } from "@/app/utils/helpers/format/date";
import { cn } from "@/lib/utils";

type DateInfoPropsT = {
  /** Creation date string */
  createdAt: string | null;
  /** Optional update date string */
  updatedAt?: string;
  /** Optional CSS classes for container */
  className?: string;
  /** Custom labels for dates */
  labels?: {
    created?: string;
    updated?: string;
  };
  /** Format options for date display */
  dateOptions?: {
    includeTime?: boolean;
    locale?: string;
  };
};

/**
 * Date Information Component
 *
 * Displays creation and optional update dates in a consistent format.
 * Commonly used in status bars, headers, or content meta information.
 *
 * @example
 * ```tsx
 * <DateInfo
 *   createdAt="2025-08-19T10:30:00Z"
 *   updatedAt="2025-08-19T14:15:00Z"
 * />
 *
 * // With custom labels
 * <DateInfo
 *   createdAt="2025-08-19T10:30:00Z"
 *   labels={{ created: "Published", updated: "Last edited" }}
 * />
 * ```
 */
export function DateInfo({
  createdAt,
  updatedAt,
  className,
  labels = {
    created: "Erstellt:",
    updated: "Zuletzt geändert:",
  },
  dateOptions,
}: DateInfoPropsT) {
  const createdFullLabel = labels.created ?? "Erstellt:";
  const updatedFullLabel = labels.updated ?? "Zuletzt geändert:";
  const createdLabel = createdFullLabel.replace(/:\s*$/, "");
  const updatedLabel = updatedFullLabel.replace(/:\s*$/, "");

  return (
    <div
      className={cn(
        "grid grid-cols-2 gap-x-4 gap-y-2 min-[1101px]:flex min-[1101px]:flex-wrap min-[1101px]:items-center min-[1101px]:gap-6",
        className
      )}
    >
      {/* Creation Date */}
      {createdAt && (
        <div className="flex min-w-0 flex-col gap-1 min-[1101px]:flex-row min-[1101px]:items-center min-[1101px]:gap-2">
          <span className="text-xs text-gray-500 min-[1101px]:text-[inherit]">
            <span className="min-[1101px]:hidden">{createdLabel}</span>
            <span className="hidden min-[1101px]:inline">
              {createdFullLabel}
            </span>
          </span>
          <span className="font-semibold text-gray-700">
            {formatDate(createdAt, dateOptions)}
          </span>
        </div>
      )}

      {/* Update Date */}
      {updatedAt && (
        <div className="flex min-w-0 flex-col gap-1 min-[1101px]:flex-row min-[1101px]:items-center min-[1101px]:gap-2">
          <span className="text-xs text-gray-500 min-[1101px]:text-[inherit]">
            <span className="min-[1101px]:hidden">{updatedLabel}</span>
            <span className="hidden min-[1101px]:inline">
              {updatedFullLabel}
            </span>
          </span>
          <span className="font-semibold text-gray-700">
            {formatDate(updatedAt, dateOptions)}
          </span>
        </div>
      )}
    </div>
  );
}
